export const ONE_TAP_DEFAULT_PREFERENCES = {
  enabled: true,
  platforms: [],
  postsPerDay: 1,
  timeSlots: ['16:00'],
};

function storageKey(userId) {
  return userId ? `makereels-one-tap-schedule-${userId}` : 'makereels-one-tap-schedule';
}

export function cacheOneTapPreferences(userId, preferences) {
  if (!userId || !preferences) return;
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(preferences));
  } catch {
    /* ignore */
  }
}

export function loadCachedOneTapPreferences(userId) {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return null;
    return normalizeOneTapPreferences(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function normalizeOneTapPreferences(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const postsPerDay = [1, 2, 3].includes(raw.postsPerDay ?? raw.posts_per_day)
    ? (raw.postsPerDay ?? raw.posts_per_day)
    : ONE_TAP_DEFAULT_PREFERENCES.postsPerDay;
  const timeSlots = Array.isArray(raw.timeSlots || raw.time_slots)
    ? (raw.timeSlots || raw.time_slots).filter((slot) => /^\d{2}:\d{2}$/.test(slot))
    : ONE_TAP_DEFAULT_PREFERENCES.timeSlots;
  const platforms = Array.isArray(raw.platforms)
    ? raw.platforms.filter(Boolean)
    : ONE_TAP_DEFAULT_PREFERENCES.platforms;
  const enabled = raw.enabled !== false;
  return {
    enabled,
    platforms,
    postsPerDay,
    timeSlots: timeSlots.slice(0, postsPerDay),
    timezoneOffsetMinutes: Number.isFinite(raw.timezoneOffsetMinutes ?? raw.timezone_offset_minutes)
      ? (raw.timezoneOffsetMinutes ?? raw.timezone_offset_minutes)
      : new Date().getTimezoneOffset(),
  };
}

export async function fetchOneTapPreferences(getToken, userId) {
  const token = await getToken();
  if (!token) return loadCachedOneTapPreferences(userId);

  try {
    const res = await fetch('/api/schedule/one-tap-preferences', {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      return loadCachedOneTapPreferences(userId);
    }
    const data = await res.json();
    const preferences = normalizeOneTapPreferences(data.preferences);
    if (preferences && userId) {
      cacheOneTapPreferences(userId, preferences);
    }
    return preferences;
  } catch {
    return loadCachedOneTapPreferences(userId);
  }
}

export async function saveOneTapPreferences(getToken, userId, preferences) {
  const token = await getToken();
  if (!token) throw new Error('Please sign in again.');

  const payload = {
    enabled: preferences.enabled !== false,
    platforms: preferences.platforms,
    posts_per_day: preferences.postsPerDay,
    time_slots: preferences.timeSlots,
    timezone_offset_minutes: preferences.timezoneOffsetMinutes ?? new Date().getTimezoneOffset(),
  };

  const res = await fetch('/api/schedule/one-tap-preferences', {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || 'Could not save preferences.');
  }

  const saved = normalizeOneTapPreferences(data.preferences || payload);
  if (saved && userId) {
    cacheOneTapPreferences(userId, saved);
  }
  return saved;
}

export async function oneTapPublishClip(getToken, { fileStem, clipFilename, taskId = null }) {
  const token = await getToken();
  if (!token) throw new Error('Please sign in again.');

  const res = await fetch('/api/schedule/one-tap-publish', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      file_stem: fileStem,
      clip_filename: clipFilename,
      task_id: taskId,
    }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const error = new Error(data.error || '1-Tap publish failed.');
    error.code = data.code;
    throw error;
  }
  return data;
}

export function buildScheduleClipCalendarUrl({ fileStem, clipFilename, taskId = null }) {
  const params = new URLSearchParams({
    scheduleClip: '1',
    fileStem,
    clipFilename,
  });
  if (taskId) params.set('taskId', taskId);
  return `/calender?${params.toString()}`;
}

export function hasValidOneTapPreferences(preferences) {
  return Boolean(
    preferences
    && preferences.enabled !== false
    && preferences.platforms?.length > 0
    && preferences.timeSlots?.length === preferences.postsPerDay,
  );
}

function oneTapScheduledClipsKey(userId) {
  return userId ? `makereels-one-tap-scheduled-clips-${userId}` : 'makereels-one-tap-scheduled-clips';
}

export function oneTapScheduledClipKey({ fileStem = '', clipFilename = '', taskId = null }) {
  return `${taskId || ''}|${fileStem}|${clipFilename}`;
}

export function loadOneTapScheduledClipKeys(userId) {
  try {
    const raw = localStorage.getItem(oneTapScheduledClipsKey(userId));
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    return new Set(Array.isArray(parsed) ? parsed : []);
  } catch {
    return new Set();
  }
}

export function markClipOneTapScheduled(userId, clipKey) {
  if (!userId || !clipKey) return;
  const keys = loadOneTapScheduledClipKeys(userId);
  keys.add(clipKey);
  try {
    localStorage.setItem(oneTapScheduledClipsKey(userId), JSON.stringify([...keys]));
  } catch {
    /* ignore */
  }
}

export function isClipOneTapScheduled(userId, { fileStem = '', clipFilename = '', taskId = null }) {
  const clipKey = oneTapScheduledClipKey({ fileStem, clipFilename, taskId });
  return loadOneTapScheduledClipKeys(userId).has(clipKey);
}
